import { logger } from '@adornis/base/logging.js';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook for managing recovery/sync when the window regains focus after being hidden.
 *
 * Problem: While the window is hidden, the backend may have completed operations
 * (like the Structure Agent finishing), and WebSocket messages might be lost due
 * to browser throttling. When the window comes back into focus, the UI doesn't
 * know about these changes.
 *
 * Solution: This hook detects when the window becomes visible again and triggers
 * a state sync/refetch operation to ensure the UI is up-to-date.
 *
 * Usage:
 * ```tsx
 * const { triggerRecovery } = useVisibilityRecovery({
 *   shouldRecover: () => isExtracting, // Only recover if we expect background work
 *   onRecover: async () => {
 *     // Re-fetch exam state
 *     await onExamRefresh?.();
 *   },
 * });
 *
 * // In your subscription handler:
 * useEffect(() => {
 *   if (document.hidden) {
 *     // Start buffering messages
 *   } else if (wasHidden) {
 *     triggerRecovery();
 *   }
 * }, [document.hidden]);
 * ```
 */
export interface VisibilityRecoveryOptions {
  /**
   * Function that returns whether recovery should be attempted.
   * E.g., return true if we expect background work to have completed.
   */
  shouldRecover?: () => boolean;

  /**
   * Callback to execute for state recovery/sync.
   * E.g., refetch exam state, check for new messages, etc.
   */
  onRecover?: () => Promise<void>;

  /**
   * Minimum time (ms) to wait before allowing recovery again.
   * Prevents flooding with recovery requests.
   */
  recoveryDebounceMs?: number;

  /**
   * Optional callback when recovery starts
   */
  onRecoveryStart?: () => void;

  /**
   * Optional callback when recovery completes
   */
  onRecoveryComplete?: () => void;

  /**
   * Optional callback when recovery fails
   */
  onRecoveryError?: (err: Error) => void;
}

export function useVisibilityRecovery(options: VisibilityRecoveryOptions) {
  const [wasHidden, setWasHidden] = useState(document.hidden);
  const [isRecovering, setIsRecovering] = useState(false);
  const lastRecoveryTimeRef = useRef<number>(0);
  const debounceMs = options.recoveryDebounceMs ?? 500;

  /**
   * Trigger recovery manually
   */
  const triggerRecovery = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastRecovery = now - lastRecoveryTimeRef.current;

    if (timeSinceLastRecovery < debounceMs) {
      logger.debug({ timeSinceLastRecovery, debounceMs, context: 'useVisibilityRecovery' }, 'Recovery debounced');
      return;
    }

    if (!options.shouldRecover?.()) {
      logger.debug({ context: 'useVisibilityRecovery' }, 'Recovery not needed (shouldRecover returned false)');
      return;
    }

    if (isRecovering) {
      logger.debug({ context: 'useVisibilityRecovery' }, 'Recovery already in progress');
      return;
    }

    setIsRecovering(true);
    lastRecoveryTimeRef.current = now;

    try {
      logger.info({ context: 'useVisibilityRecovery' }, 'Starting recovery');
      options.onRecoveryStart?.();

      await options.onRecover?.();

      logger.info({ context: 'useVisibilityRecovery' }, 'Recovery completed');
      options.onRecoveryComplete?.();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error({ err: error, context: 'useVisibilityRecovery' }, 'Recovery failed');
      options.onRecoveryError?.(error);
    } finally {
      setIsRecovering(false);
    }
  }, [options, debounceMs, isRecovering]);

  /**
   * Listen for visibility changes and trigger recovery
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        logger.debug({ context: 'useVisibilityRecovery' }, 'Window hidden');
        setWasHidden(true);
      } else {
        logger.debug({ context: 'useVisibilityRecovery' }, 'Window visible again');
        if (wasHidden) {
          void triggerRecovery();
        }
        setWasHidden(false);
      }
    };

    // Also handle focus events as backup
    const handleFocus = () => {
      logger.debug({ context: 'useVisibilityRecovery' }, 'Window focus event');
      void triggerRecovery();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [wasHidden, triggerRecovery]);

  return {
    /**
     * Manually trigger recovery
     */
    triggerRecovery,

    /**
     * Whether recovery is currently in progress
     */
    isRecovering,

    /**
     * Whether the window was hidden at any point
     */
    wasHidden,
  };
}
